import { lazy, ComponentType, LazyExoticComponent } from 'react'

interface RetryOptions {
  retries?: number
  delay?: number
}

type LazyWithRetry<T extends ComponentType<any>> = LazyExoticComponent<T> & {
  preload: () => Promise<{ default: T }>
}

/**
 * Distinguishes a chunk that could not be *fetched* from one that downloaded
 * and then threw while evaluating.
 *
 * Only the former is worth reloading for: it means a newer deploy replaced the
 * hashed bundles while this client held a stale index.html, and a reload picks
 * up the new shell. An evaluation error is a code bug - the module record
 * caches the failure, so a reload fails identically while discarding state.
 *
 * Each engine words this differently, and the SPA fallback rewrites a missing
 * chunk to index.html, so the miss can also surface as an HTML MIME type.
 */
const MODULE_LOAD_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module', // Chrome, Edge
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // Safari
  'unable to load module', // older Safari
  'failed to load module script', // Chrome, wrong content type
  'expected a javascript module script', // SPA fallback served index.html
  'mime type', // generic content-type mismatch
]

function isModuleLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
      ? error
      : ''
  const normalized = message.toLowerCase()
  return MODULE_LOAD_ERROR_PATTERNS.some((pattern) =>
    normalized.includes(pattern)
  )
}

/**
 * Creates a React.lazy component with retry logic for failed dynamic imports
 * @param factory - The dynamic import function
 * @param options - Retry configuration
 * @returns LazyExoticComponent with preload capability
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  options: RetryOptions = {}
): LazyWithRetry<T> {
  const { retries = 3, delay = 1000 } = options

  const retryFactory = async (attemptNumber = 0): Promise<{ default: T }> => {
    try {
      return await factory()
    } catch (error) {
      console.warn(
        `Dynamic import failed (attempt ${attemptNumber + 1}/${retries + 1}):`,
        error
      )

      if (attemptNumber < retries) {
        // Add exponential backoff delay
        const backoffDelay = delay * 2 ** attemptNumber
        await new Promise((resolve) => setTimeout(resolve, backoffDelay))
        return retryFactory(attemptNumber + 1)
      }

      // Retries are exhausted. If the chunk could not be fetched, it is most
      // likely gone because a newer deploy replaced the hashed bundles while
      // this client held a stale index.html, and reloading revalidates the
      // shell and fetches the new bundles.
      console.error('All dynamic import retries failed:', error)

      // Only reload once the guard is durably stored. Safari private mode lets
      // getItem() succeed while setItem() throws, so reloading on a failed
      // write would loop forever. Same key as the startup handler in
      // index.html, so the two paths share a single reload per session.
      // A module that downloaded but threw while evaluating is a code bug, not
      // a stale bundle - reloading cannot fix it, so let it surface.
      let shouldReload = false
      if (isModuleLoadError(error)) {
        try {
          if (!sessionStorage.getItem('stale-asset-reload')) {
            sessionStorage.setItem('stale-asset-reload', '1')
            shouldReload = true
          }
        } catch {
          // sessionStorage unavailable (private mode) - fall through and throw
        }
      }

      if (shouldReload) {
        // Clear any cached chunks and reload
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then((registrations) => {
            registrations.forEach((registration) => registration.unregister())
          })
        }

        // Clear browser cache for this origin
        if ('caches' in window) {
          caches.keys().then((names) => {
            names.forEach((name) => caches.delete(name))
          })
        }

        // Reload the page after a short delay
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      }

      throw error
    }
  }

  const Component = lazy(retryFactory) as LazyWithRetry<T>
  Component.preload = retryFactory
  return Component
}

/**
 * Standard lazyWithPreload function for components that don't need retry logic
 */
export function lazyWithPreload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyWithRetry<T> {
  const Component = lazy(factory) as LazyWithRetry<T>
  Component.preload = factory
  return Component
}
